import { MessageSquare, TrendingDown, AlertTriangle, LayoutGrid, Phone, RotateCw, Gavel, Plus } from 'lucide-react';

export const MOTIF_CHIP = {
  panneaux_insuffisants: 'bg-red-50 text-red-600',
  emplacement_pas_interessant: 'bg-red-50 text-red-600',
  cout_eleve: 'bg-amber-50 text-amber-700',
  duree_engagement: 'bg-blue-50 text-blue-700',
  budget_gele: 'bg-blue-50 text-blue-700',
  autre: 'bg-slate-100 text-slate-600',
};

export const riskTone = (s) => (s >= 60 ? { hex: '#ef4444', text: 'text-red-500', bg: 'bg-red-50' }
  : s >= 30 ? { hex: '#f59e0b', text: 'text-amber-500', bg: 'bg-amber-50' }
    : { hex: '#16a34a', text: 'text-green-600', bg: 'bg-green-50' });

const shadow = 'shadow-[0_2px_12px_rgba(15,23,42,0.06)]';

export function LitigeGauge({ data }) {
  const taux = data?.taux || 0;
  const frac = Math.min(1, taux / 20);
  const r = 110, cx = 140, cy = 135, len = Math.PI * r;
  const theta = Math.PI * (1 - frac);
  const nx = cx + (r - 22) * Math.cos(theta), ny = cy - (r - 22) * Math.sin(theta);
  const level = taux < 5 ? { label: 'Risque faible', cls: 'text-green-600' } : taux <= 10 ? { label: 'Risque modéré', cls: 'text-amber-500' } : { label: 'Risque élevé', cls: 'text-red-500' };
  const delta = data?.delta_pts || 0;
  return (
    <div className={`bg-white rounded-2xl p-6 ${shadow} h-full`} data-testid="litige-gauge-card">
      <h3 className="text-lg font-bold text-[#0f1a3a]">Taux de litige</h3>
      <p className="text-sm text-slate-500">Litiges ouverts ÷ dossiers traités · 90 derniers jours</p>
      <div className="flex justify-center mt-2">
        <svg viewBox="0 0 280 160" className="w-72">
          <defs>
            <linearGradient id="gaugeGrad" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#16a34a" />
              <stop offset="50%" stopColor="#eab308" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
          </defs>
          <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#eef2f7" strokeWidth="22" strokeLinecap="round" />
          <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="url(#gaugeGrad)" strokeWidth="22" strokeLinecap="round"
            strokeDasharray={len} strokeDashoffset={len * (1 - frac)} style={{ transition: 'stroke-dashoffset 900ms ease-out' }} />
          <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#0f1a3a" strokeWidth="4" strokeLinecap="round" />
          <circle cx={cx} cy={cy} r="8" fill="#0f1a3a" />
        </svg>
      </div>
      <div className="text-center -mt-2">
        <div className="text-5xl font-extrabold text-[#0f1a3a] tracking-tight" data-testid="litige-taux">{String(taux).replace('.', ',')} %</div>
        <div className={`font-bold ${level.cls}`} data-testid="litige-level">{level.label}</div>
      </div>
      <div className="flex justify-center gap-4 mt-3 text-xs text-slate-500">
        <span><span className="inline-block w-2 h-2 rounded-full bg-green-600 mr-1" />Sain &lt; 5 %</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1" />Surveillé 5–10 %</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1" />Critique &gt; 10 %</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-5">
        <MiniStat value={data?.litiges_ouverts ?? 0} label="Litiges ouverts" testid="litige-ouverts" />
        <MiniStat value={data?.dossiers_traites ?? 0} label="Dossiers traités" testid="litige-traites" />
        <MiniStat value={`${delta > 0 ? '+' : ''}${String(delta).replace('.', ',')} pt`} label="vs mois dernier" testid="litige-delta" cls={delta > 0 ? 'text-red-500' : 'text-green-600'} />
      </div>
    </div>
  );
}

const MiniStat = ({ value, label, testid, cls = 'text-[#0f1a3a]' }) => (
  <div className="bg-slate-50 rounded-xl px-3 py-2.5" data-testid={testid}>
    <div className={`text-xl font-bold ${cls}`}>{value}</div>
    <div className="text-xs text-slate-500">{label}</div>
  </div>
);

export function RetourKpis({ stats }) {
  const items = [
    { icon: MessageSquare, tint: 'bg-indigo-50 text-indigo-600', value: stats.retours_ce_mois, label: 'Retours ce mois', id: 'kpi-retours-mois' },
    { icon: TrendingDown, tint: 'bg-rose-50 text-rose-500', value: stats.prospects_perdus, label: 'Prospects perdus', id: 'kpi-prospects-perdus' },
    { icon: AlertTriangle, tint: 'bg-amber-50 text-amber-500', value: stats.motif_principal, label: 'Motif principal', id: 'kpi-motif-principal', text: true },
    { icon: LayoutGrid, tint: 'bg-green-50 text-green-600', value: stats.panneaux_insuffisants, label: 'Panneaux insuffisants', id: 'kpi-panneaux-insuffisants' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map(({ icon: Icon, ...k }, i) => (
        <div key={k.id} className={`bg-white rounded-2xl p-5 ${shadow} cs-rise`} style={{ animationDelay: `${i * 60}ms` }} data-testid={k.id}>
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${k.tint}`}><Icon className="h-5 w-5" /></div>
          {k.text
            ? <div className="mt-4 text-lg font-bold text-[#0f1a3a] leading-snug">{k.value}</div>
            : <div className="mt-4 text-3xl font-extrabold text-[#0f1a3a]">{k.value}</div>}
          <div className="text-sm text-slate-500 mt-1">{k.text ? 'Motif principal' : k.label}</div>
        </div>
      ))}
    </div>
  );
}

export function RiskFactors({ facteurs = [] }) {
  const color = (s) => (s >= 60 ? '#ef4444' : s >= 30 ? '#f59e0b' : '#16a34a');
  return (
    <div className={`bg-white rounded-2xl p-6 ${shadow}`} data-testid="risk-factors">
      <h3 className="text-lg font-bold text-[#0f1a3a]">Facteurs de risque de litige</h3>
      <p className="text-sm text-slate-500">Calculés à partir des motifs de refus, des délais et de l'historique</p>
      <div className="mt-4 space-y-3">
        {facteurs.map((f) => (
          <div key={f.label} className="grid grid-cols-[180px_1fr_36px] items-center gap-4">
            <span className="text-sm text-slate-800">{f.label}</span>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${f.score}%`, background: color(f.score) }} />
            </div>
            <span className="text-sm font-bold text-[#0f1a3a] text-right">{f.score}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RiskRing({ score }) {
  const t = riskTone(score);
  const r = 34, c = 2 * Math.PI * r;
  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#eef2f7" strokeWidth="7" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={t.hex} strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-xl font-extrabold ${t.text}`}>{score}</span>
        <span className="text-[9px] text-slate-400 -mt-1">risque</span>
      </div>
    </div>
  );
}

const STEPS = ['Contact', 'Devis', 'Retour', 'Relance', 'Décision'];

function Timeline({ r }) {
  const done = [true, !!r.has_devis, true, !!r.derniere_relance, !!r.decision];
  const cur = done.indexOf(false);
  const lastDone = done.lastIndexOf(true);
  return (
    <div className="relative grid grid-cols-5 mt-5" data-testid={`retour-timeline-${r.id}`}>
      <div className="absolute top-[7px] left-[10%] right-[10%] h-0.5 bg-slate-200" />
      <div className="absolute top-[7px] left-[10%] h-0.5 bg-blue-600 transition-[width] duration-700" style={{ width: `${lastDone * 20}%` }} />
      {STEPS.map((s, i) => {
        const state = done[i] ? 'done' : i === cur ? 'cur' : 'todo';
        return (
          <div key={s} className="relative flex flex-col items-center">
            <span className={`w-4 h-4 rounded-full ring-4 ring-white ${state === 'done' ? 'bg-blue-600' : state === 'cur' ? 'bg-amber-500' : 'bg-slate-300'}`} />
            <span className={`text-xs mt-1.5 ${state === 'done' ? 'font-bold text-[#0f1a3a]' : 'text-slate-500'}`}>{s}</span>
          </div>
        );
      })}
    </div>
  );
}

const PRIO = { haute: ['Haute', 'text-red-500'], moyenne: ['Moyenne', 'text-amber-500'], basse: ['Basse', 'text-green-600'] };

export function RetourCard({ r, motifLabel, timeAgo, onCall, onRelance, onOpen, onLitige, delay }) {
  const t = riskTone(r.risk_score || 0);
  const motifs = r.motifs || [];
  const initials = (r.client_name || '?').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const [prioLabel, prioCls] = PRIO[r.priorite] || PRIO.moyenne;
  const gap = (r.panneaux_demandes || 0) > (r.panneaux_proposes || 0);
  const fields = [
    ['Commercial', r.commercial_name || r.created_by || '—'],
    ['Date du retour', timeAgo(r.created_at)],
    ['Canal', r.canal || '—'],
    ['Contact', r.contact || '—'],
    ['Panneaux demandés', r.panneaux_demandes || '—'],
    ['Panneaux proposés', r.panneaux_proposes || '—', gap ? t.text : ''],
    ['Budget proposé', r.budget_propose ? `${new Intl.NumberFormat('fr-FR').format(r.budget_propose)} FCFA` : '—'],
    ['Dernière relance', r.derniere_relance ? timeAgo(r.derniere_relance) : '—'],
    ['Priorité', prioLabel, prioCls],
  ];
  return (
    <div className={`bg-white rounded-2xl p-7 ${shadow} border-t-4 cs-rise`} style={{ borderTopColor: t.hex, animationDelay: `${delay}ms` }} data-testid={`retour-card-${r.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-extrabold ${t.bg} ${t.text}`}>{initials}</div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xl font-bold text-[#0f1a3a] truncate">{r.client_name}</span>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${r.type === 'Prospect' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-700'}`}>{r.type}</span>
              {r.litige_ouvert && <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-red-50 text-red-600" data-testid={`retour-litige-badge-${r.id}`}>Litige ouvert</span>}
            </div>
            <div className="text-sm text-slate-500">{[r.secteur, r.zone].filter(Boolean).join(' · ') || '—'}</div>
          </div>
        </div>
        <div data-testid={`retour-risk-${r.id}`}><RiskRing score={r.risk_score || 0} /></div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4 pb-4 border-b border-slate-100">
        {motifs.slice(0, 3).map((m, i) => (
          <span key={i} className={`text-xs font-bold px-3 py-1.5 rounded-lg ${MOTIF_CHIP[m.motif] || MOTIF_CHIP.autre}`}>{motifLabel(m.motif)}</span>
        ))}
        {motifs.length > 3 && <span className="text-xs font-bold px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700">+{motifs.length - 3} {motifLabel(motifs[3].motif).split(' ')[0]}</span>}
      </div>

      <div className="grid grid-cols-3 gap-x-4 gap-y-3 py-4 border-b border-slate-100">
        {fields.map(([k, v, cls]) => (
          <div key={k}>
            <div className="text-xs text-slate-500">{k}</div>
            <div className={`text-sm font-bold ${cls || 'text-[#0f1a3a]'}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 bg-slate-50 rounded-xl px-4 py-3">
        <div className="text-xs font-bold text-slate-500">Commentaire</div>
        <p className="text-sm text-slate-700 mt-1">{r.commentaire || 'Aucun commentaire renseigné. Ajoutez le détail du retour pour améliorer le suivi.'}</p>
      </div>

      <Timeline r={r} />

      <div className="grid grid-cols-4 gap-2 mt-5">
        <button onClick={onCall} className="h-11 rounded-xl border border-slate-200 text-sm font-bold text-[#0f1a3a] hover:bg-slate-50 flex items-center justify-center gap-1.5 transition-colors" data-testid={`retour-call-${r.id}`}><Phone className="h-4 w-4" /> Appeler</button>
        <button onClick={onRelance} className="h-11 rounded-xl border border-slate-200 text-sm font-bold text-[#0f1a3a] hover:bg-slate-50 flex items-center justify-center gap-1.5 transition-colors" data-testid={`retour-relance-${r.id}`}><RotateCw className="h-4 w-4" /> Relancer</button>
        <button onClick={onOpen} className="h-11 rounded-xl bg-blue-700 text-white text-sm font-bold hover:bg-blue-800 transition-colors" data-testid={`retour-open-${r.id}`}>Voir le dossier</button>
        <button onClick={onLitige} className="h-11 rounded-xl border border-slate-200 text-sm font-bold text-red-500 hover:bg-red-50 flex items-center justify-center gap-1.5 transition-colors" data-testid={`retour-litige-${r.id}`}><Gavel className="h-4 w-4" /> Litige</button>
      </div>
    </div>
  );
}

export function AddRetourCard({ onClick }) {
  return (
    <button onClick={onClick} className="w-full rounded-2xl border-2 border-dashed border-slate-300 py-10 flex flex-col items-center hover:border-blue-400 hover:bg-white/60 transition-colors group" data-testid="add-retour-card">
      <span className="w-14 h-14 rounded-full bg-white shadow flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform"><Plus className="h-6 w-6" /></span>
      <span className="mt-3 font-bold text-[#0f1a3a]">Ajouter un retour client</span>
      <span className="text-sm text-slate-500">Chaque retour alimente le taux de litige et les motifs de refus.</span>
    </button>
  );
}
